import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { livechat } from '../../../libs/livechat_ref.js';
import packageInfo from '../../../../package.json';
import { DomSanitizer } from '@angular/platform-browser';
declare function validateConfig(config: any): any;
import { TranslocoService } from '@ngneat/transloco';
import { environment } from 'src/environments/environment';

declare var window: any;
@Injectable({
  providedIn: 'root'
})
export class AppdataService {
  private appConfig = null;
  private configSubject: Subject<any> = new Subject<any>();
  private appLabels = null;
  labelSubject: Subject<any> = new ReplaySubject<any>();
  private appVersion: string;
  public getUserMediaFn =
    window?.navigator &&
    ((window?.navigator?.mediaDevices && window?.navigator?.mediaDevices?.getUserMedia) ||
      window?.navigator?.getUserMedia ||
      window?.navigator?.webkitGetUserMedia ||
      window?.navigator?.mozGetUserMedia ||
      window?.navigator?.msGetUserMedia);

  labelObservable: Observable<any> = this.labelSubject.asObservable();

  constructor(private translateService: TranslocoService, private sanitizer: DomSanitizer) {
    this.configSubject = new Subject();
    this.labelSubject = new Subject();
    this.appVersion = packageInfo.version;

    this.translateService.langChanges$.subscribe((data: any) => {
      console.log('Visual IVR Configurations :', data);
      this.setApplicationLabels(data);
    });
  }

  /**
   * Get App Config
   */
  get appConfigSub(): Observable<any> {
    return this.configSubject.asObservable();
  }

  setConfig(config: any) {
    let appConfig = config;
    try {
      appConfig = validateConfig(config);
    } catch {}
    this.appConfig = appConfig;
    this.configSubject.next(appConfig);
  }

  /**
   * Method to change the application labels change
   * @param lang Language code to be changed
   */
  setLanguage(lang: string): void {
    try {
      this.translateService.setActiveLang(lang);
    } catch (error) {
      console.error(error)
    }
  }

  /**
   * Method to set the application labels of specific language
   * @param locale Locale of the application labels
   */
  setApplicationLabels(locale: string): void {
    try {
      this.translateService.load(locale).subscribe((data) => {
        console.log(`Labels initiated in ${locale} locale!`);
        this.appLabels = data;
        this.labelSubject.next(this.appLabels);
      });
    } catch (error) {
      console.error(error);
    }
  }

  getConfig() {
    return { ...this.appConfig };
  }

  setLabels(labels: any) {
    this.appLabels = labels;
    this.labelSubject.next(labels);
  }

  getLabels() {
    return { ...this.appLabels };
  }

  /**
   * method to get app version
   * @returns app version as a string
   */
  getAppVersion(): string {
    if(!environment.production) {
      return this.appConfig?.CommunicationInterface?.activeLiveChat + ": " + this.appVersion;
    }
    return this.appVersion;

  }

  /**
   * Method to log package data to server
   */
  logPackagesToServer(): void {
    try {
      if (this.appConfig?.Miscellaneous?.logPackagesToServer) {
        let formattedPackageString = `Package Name                                 Version\n`;
        Object.keys(packageInfo.dependencies).forEach(function (key) {
          formattedPackageString += key.padEnd(45, '.') + packageInfo.dependencies[key] + '\n';
        });
        livechat?.slog(
          'info',
          `[VIVR.AppdataService.appendSourceFiles - Logging packages which are dependent on normal application running]`,
          `${formattedPackageString}`
        );
      }
    } catch (error) {
      console.error('%c Error Occured in constructor', 'color: red', error);
      livechat?.slog('error', 'Error occured in AppComponent.constructor', JSON.stringify(error));
    }
  }

  /**
   * method to append custom source files to the application before even starts
   * @returns a resolved promise after appending source files
   */
  async appendSourceFiles(): Promise<boolean> {
    var appConfig;
    var url = 'assets/app-config.json';
    var ok = await fetch(url);
    appConfig = await ok.json();

    // if(appConfig.CommunicationInterface?.activeLiveChat.toLowerCase() == "livechat1") {
    //   var overridePath = environment.production ? '../api' : 'https://localhost:15012/api';
    //   let apilUrl = environment.production ? ".." : "https://localhost:15012";
    //   let localLiveChat1Files =  [
    //     {
    //         "type": "text/javascript",
    //         "src": apilUrl + "/api/livechat/livechat.js",
    //         "crossOrigin": "anonymous"
    //     },
    //     {
    //         "type": "text/javascript",
    //         "src": apilUrl + "/api/livechat/interface/__config.js",
    //         "crossOrigin": "anonymous"
    //     },
    //     {
    //         "type": "text/javascript",
    //         "src": apilUrl + "/api/livechat/interface/__clientlog.js",
    //         "crossOrigin": "anonymous"
    //     }
    //   ];   
    //   localLiveChat1Files.forEach((lc1file: any, index: number) => {
    //     var script = document.createElement('script');
    //     script.type = lc1file.type;
    //     script.src = lc1file.src + '?version=' + this.getAppVersion();
    //     script.crossOrigin = lc1file.crossOrigin;
    //     document.head.appendChild(script);
    // });
    // } else {
    //   var script = document.createElement('script');
    //     script.type = "text/javascript";
    //     script.src = "assets/api/livechat2.wrapper.js" + '?version=' + this.getAppVersion();
    //     script.crossOrigin = "anonymous";
    //     document.head.appendChild(script);
    // }

    return new Promise<boolean>((resolve, reject) => {
      if (!appConfig?.enableConsole) {
        console.clear();
        if (window) window.console.log = () => {};
      }
      appConfig.customSources.forEach((customSource: any, index: number) => {
        if (customSource.type == 'stylesheet') {
          var link = document.createElement('link');
          link.rel = customSource.type;
          link.href = customSource.src + '?version=' + this.getAppVersion();
          link.crossOrigin = customSource.crossOrigin;
          document.body.appendChild(link);
        } else if (customSource.type == 'text/javascript') {
          var script = document.createElement('script');
          script.type = customSource.type;
          script.src = customSource.src + '?version=' + this.getAppVersion();
          script.crossOrigin = customSource.crossOrigin;
          document.head.appendChild(script);
        }
      });

      setTimeout(() => {
        if (appConfig.Miscellaneous.UILogger.enable) {
          var script = document.createElement('script');
          script.type = 'text/javascript';
          script.src = appConfig.Miscellaneous.UILogger.sdkLocation + '?version=' + this.getAppVersion();
          script.crossOrigin = 'anonymous';
          document.body.appendChild(script).onload = function () {
            resolve(true);
          };
        } else resolve(true);
      }, 500);
    });
  }

  /**
   * Method to sanitize content into safe html
   * @param content Content to be sanitized
   * @returns Sanitized content (safe html)
   */
  getSanitizedContent(content): any {
    return this.sanitizer.bypassSecurityTrustHtml(content)
  }
}
